import { z } from 'zod';
import type { JobRunStatus } from '../enums.js';

/**
 * Contrat réseau des tâches planifiées. Ces routes sont réservées à l'exploitation de la
 * plateforme : déclencher une tâche à la main et relire ses exécutions.
 *
 * Le détail d'un passage, quand la tâche en laisse un, ne porte que le nom du cabinet et une
 * issue. Jamais un courriel, une clé, un message envoyé ou un identifiant de patient.
 */

/**
 * Issue d'un cabinet pendant un passage. `succeeded`, `skipped` et `unreadable` sont celles de
 * Doctena ; `sent`, `nothing_to_send` et `error` celles des rappels. `skipped` sert aussi quand
 * le passage n'a rien fait pour ce cabinet.
 */
export const jobAccountOutcomes = [
  'succeeded',
  'skipped',
  'unreadable',
  'sent',
  'nothing_to_send',
  'error',
] as const;
export type JobAccountOutcome = (typeof jobAccountOutcomes)[number];

/** Une ligne du détail : le cabinet, et rien qui permette de joindre un compte ou un patient. */
export const jobAccountLineSchema = z
  .object({
    organizationName: z.string().trim().min(1).max(200),
    /** Présent pour Doctena : le cabinet distant est-il choisi ? Absent pour les rappels. */
    practiceChosen: z.boolean().optional(),
    outcome: z.enum(jobAccountOutcomes),
    items: z.number().int().nonnegative().max(1_000_000).optional(),
  })
  .strict();
export type JobAccountLine = z.infer<typeof jobAccountLineSchema>;

export const jobRunDetailSchema = z
  .object({
    accounts: z.array(jobAccountLineSchema).max(200),
  })
  .strict();
export type JobRunDetail = z.infer<typeof jobRunDetailSchema>;

/** Le nom est validé contre le registre côté serveur : ici, seulement une forme sûre. */
export const jobNameParamSchema = z
  .object({
    name: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9-]{2,63}$/, 'Nom de tâche invalide'),
  })
  .strict();
export type JobNameParam = z.infer<typeof jobNameParamSchema>;

export const jobRunsQuerySchema = z
  .object({
    name: z.string().trim().max(64).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type JobRunsQuery = z.infer<typeof jobRunsQuerySchema>;

export interface JobRunDto {
  id: string;
  jobName: string;
  startedAt: string;
  finishedAt: string | null;
  status: JobRunStatus;
  itemsProcessed: number;
  /** Message technique tronqué, sans donnée personnelle. */
  error: string | null;
  /**
   * Cabinets de ce passage. `null` : la tâche n'en dresse pas la liste (purge, rapprochement).
   * Tableau vide : elle en dresse une, et aucun cabinet n'était activé.
   */
  detail: JobRunDetail | null;
}

export interface JobRunListDto {
  items: JobRunDto[];
}

/** Résultat d'un déclenchement manuel. */
export interface JobRunSummaryDto {
  jobName: string;
  status: JobRunStatus;
  itemsProcessed: number;
  startedAt: string;
  finishedAt: string;
}
